/**
 * VillainsServiceModule — 2026-06-08 §3.E wire-up for the canonical Villains
 * Supplement (Victory Games VG35011) named NPCs (Cartesia / Buonvisi /
 * Vandemere / Parkhurst / Kleiner / Prosinski / Larose Major Villains +
 * their 9 lieutenants + 10 SMERSH = 26 NPCs).
 *
 * Routes (per briefing_room_backend/routes/canonical_npcs_villains_vg35011.py):
 *   GET /api/modules/briefing-room/canonical-npcs-villains-vg35011        → VillainsNpcManifest
 *   GET /api/modules/briefing-room/canonical-npcs-villains-vg35011/{id}   → full entry | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 *
 * Distinct from CanonicalNpcsServiceModule (which wires the Core Rulebook
 * Chapter 18 file). Each sourcebook keeps its own endpoint + drawer.
 */
import { getAuthHeaders } from './authHeaders';

export type VillainsNpcCategory =
  | 'major_villain'
  | 'major_villain_lieutenant'
  | 'major_villain_lieutenant_prose_only'
  | 'smersh_leader'
  | 'smersh_agent';

export interface VillainsNpcManifestEntry {
  id: string;
  name: string;
  category: VillainsNpcCategory;
  manifest_section: string;
  source_md_line: number | null;
}

export interface VillainsNpcManifest {
  npcs: VillainsNpcManifestEntry[];
  total: number;
  /**
   * How many additional Villains-supplement NPCs are enumerated as pending
   * authoring. After §3.B + §3.B.2 + §3.C, the only entries remaining are
   * the 3 Branded Man adventure NPCs deferred to §3.J (Buhardin / Williams /
   * Corlin). SPA banner surfaces this so the seed gap is honest.
   */
  pending_remaining_count: number;
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/canonical-npcs-villains-vg35011';

export class VillainsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<VillainsNpcManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `VillainsServiceModule: list HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as VillainsNpcManifest;
  }

  async get(npcId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(npcId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(
        `VillainsServiceModule: get HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
